import { z } from "zod";

import type { CardCatalog } from "./catalog-validation.js";
import {
  CardDefinitionIdSchema,
  DeckDefinitionIdSchema,
  DefinitionVersionSchema,
  PositiveIntegerSchema,
  SCHEMA_VERSION,
  SemanticVersionSchema,
  type CardDefinitionId,
} from "./primitives.js";

export const REQUIRED_DECK_SIZE = 40 as const;
export const MAX_CARD_COPIES_PER_DECK = 3 as const;

/** Temporary FK-RULE-002 setup-termination policy; not a canonical ratio. */
export const TEMPORARY_MINIMUM_LAND_CARDS = 1 as const;

export const DeckCardEntrySchema = z
  .object({
    cardId: CardDefinitionIdSchema,
    count: PositiveIntegerSchema.max(
      MAX_CARD_COPIES_PER_DECK,
      `a deck may contain at most ${MAX_CARD_COPIES_PER_DECK} copies of one card`,
    ),
  })
  .strict();

export const DeckDefinitionSchema = z
  .object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    definitionVersion: DefinitionVersionSchema,
    cardDataVersion: SemanticVersionSchema,
    id: DeckDefinitionIdSchema,
    name: z.string().trim().min(1).max(120),
    cards: z.array(DeckCardEntrySchema).min(1),
  })
  .strict()
  .superRefine((deck, context) => {
    const totalCards = deck.cards.reduce((total, entry) => total + entry.count, 0);
    if (totalCards !== REQUIRED_DECK_SIZE) {
      context.addIssue({
        code: "custom",
        path: ["cards"],
        message: `must contain exactly ${REQUIRED_DECK_SIZE} cards; received ${totalCards}`,
      });
    }

    const firstIndexByCardId = new Map<string, number>();
    deck.cards.forEach((entry, index) => {
      const firstIndex = firstIndexByCardId.get(entry.cardId);
      if (firstIndex === undefined) {
        firstIndexByCardId.set(entry.cardId, index);
        return;
      }

      context.addIssue({
        code: "custom",
        path: ["cards", index, "cardId"],
        message: `duplicates cards[${firstIndex}].cardId '${entry.cardId}'`,
      });
    });
  });

export function validateDeck(input: unknown, catalog: CardCatalog) {
  return DeckDefinitionSchema.superRefine((deck, context) => {
    if (deck.cardDataVersion !== catalog.cardDataVersion) {
      context.addIssue({
        code: "custom",
        path: ["cardDataVersion"],
        message: `must match catalog version '${catalog.cardDataVersion}'`,
      });
    }

    const cardsById = new Map(catalog.cards.map((card) => [card.id, card]));
    let landCount = 0;

    deck.cards.forEach((entry, index) => {
      const card = cardsById.get(entry.cardId);
      if (card === undefined) {
        context.addIssue({
          code: "custom",
          path: ["cards", index, "cardId"],
          message: `references unknown catalog card '${entry.cardId}'`,
        });
        return;
      }

      if (card.type === "land") {
        landCount += entry.count;
      }
    });

    if (landCount < TEMPORARY_MINIMUM_LAND_CARDS) {
      context.addIssue({
        code: "custom",
        path: ["cards"],
        message:
          "must contain at least one Land so opening-hand setup can terminate (temporary FK-RULE-002 policy)",
      });
    }
  }).safeParse(input);
}

/**
 * Expand a structurally validated deck in its explicit, pre-shuffle entry
 * order. Setup owns stable instance creation and all later randomization.
 */
export function expandDeckDefinition(deck: DeckDefinition): readonly CardDefinitionId[] {
  return deck.cards.flatMap((entry) => Array.from({ length: entry.count }, () => entry.cardId));
}

export type DeckCardEntry = z.infer<typeof DeckCardEntrySchema>;
export type DeckDefinition = z.infer<typeof DeckDefinitionSchema>;
export type DeckValidationResult = ReturnType<typeof validateDeck>;
